import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";

import type { DropdownItemProps } from "components";
import type { TyreBrandDiscount } from "models";
import type { AddUpdateTyreBrandDiscountFormValues } from "modules/TyreDiscountsDashboard/components";

type AddUpdateTyreDiscountFormProps = {
  hasErrors: boolean;
  isPending: boolean;
  categoriesOptions: DropdownItemProps[];
  brandOptions: DropdownItemProps[];
  tyreBrandDiscount: TyreBrandDiscount | null;
  onClose: () => void;
  onSubmit: (values: AddUpdateTyreBrandDiscountFormValues) => void;
};

const FormHelper = createFormHelper<AddUpdateTyreBrandDiscountFormValues>();

export const AddUpdateTyreBrandDiscountForm = ({
  hasErrors,
  isPending,
  brandOptions,
  tyreBrandDiscount,
  categoriesOptions,
  onClose,
  onSubmit
}: AddUpdateTyreDiscountFormProps) => {
  const { t } = useTranslation();
  const { watch, handleSubmit } = FormHelper.useFormContext();

  const [brand, category, discount, netMarginEuro, netMarginPercent] = watch(["brand", "category", "discount", "net_margin_euro", "net_margin_percent"]);

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">{!tyreBrandDiscount ? t("v8_add_brand_discount", "Add Brand Discount") : t("v8_edit_brand_discount", "Edit Brand Discount")}</div>
          <div className="header-checkbox-container">
            <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} />
          </div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={brand === null && hasErrors}>
              <label>{t("v8_brand", "Brand")} </label>
              <FormHelper.Field.Dropdown name="brand" options={brandOptions} dropdownProps={{ fluid: true, search: true }} />
            </Form.Field>
          </GridColumn>

          <GridColumn>
            <Form.Field required error={!category && hasErrors}>
              <label>{t("v8_category", "Category")} </label>
              <FormHelper.Field.Dropdown name="category" options={categoriesOptions} dropdownProps={{ fluid: true, search: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>

        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={(!discount || discount > 99.99 || discount < 0) && hasErrors}>
              <label>{t("v8_discount", "Discount")}</label>
              <FormHelper.Field.Input
                name="discount"
                nullable
                inputProps={{
                  disabled: netMarginEuro != null || netMarginPercent != null,
                  placeholder: t("v8_discount", "Discount"),
                  fluid: true,
                  labelPosition: "right",
                  label: { basic: true, content: <Icon name="percent" /> },
                  type: "number"
                }}
              />
            </Form.Field>
          </GridColumn>
          <GridColumn>
            <Form.Field required error={(!netMarginEuro || netMarginEuro > 99.99 || netMarginEuro < 0) && hasErrors}>
              <label>{`${t("v8_net", "Net")} + ${t("v8_margin", "Margin")}`}</label>
              <FormHelper.Field.Input
                name="net_margin_euro"
                nullable
                inputProps={{
                  disabled: discount != null || netMarginPercent != null,
                  placeholder: t("v8_discount", "Discount"),
                  fluid: true,
                  labelPosition: "right",
                  label: { basic: true, content: <Icon name="euro" /> },
                  type: "number"
                }}
              />
            </Form.Field>
          </GridColumn>
          <GridColumn>
            <Form.Field required error={(!netMarginPercent || netMarginPercent > 99.99 || netMarginPercent < 0) && hasErrors}>
              <label>{`${t("v8_net", "Net")} + ${t("v8_percent", "Percent")}`}</label>
              <FormHelper.Field.Input
                name="net_margin_percent"
                nullable
                inputProps={{
                  disabled: discount != null || netMarginEuro != null,
                  placeholder: t("v8_discount", "Discount"),
                  fluid: true,
                  labelPosition: "right",
                  label: { basic: true, content: <Icon name="percent" /> },
                  type: "number"
                }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending} onClick={handleSubmit(d => onSubmit(d))}>
              {!tyreBrandDiscount ? t("v8_save", "Save") : t("v8_update", "Update")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
